import React, { useState } from 'react';
import { Lightbulb, Sparkles, ExternalLink, ArrowRight, Play } from 'lucide-react';
import { creationsAndLearnings } from '../data/mock';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const CreationsLearnings = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  // Gradient palette accents per category - coral → pink → purple family
  const categoryStyles = {
    'Tool': {
      gradient: 'from-[#fbf5ea] to-[#f5ebd9]',
      border: 'border-[#e0c8a8]',
      accent: '#b88865',
      accentLight: '#e0c8a8'
    },
    'Interactive': {
      gradient: 'from-[#f7eedb] to-[#efe2cd]',
      border: 'border-[#d9b896]',
      accent: '#8b5a3c',
      accentLight: '#d9b896'
    },
    'Product Thinking': {
      gradient: 'from-[#f7eedb] to-[#e8d9c0]',
      border: 'border-[#c8a787]',
      accent: '#a06d4d',
      accentLight: '#c8a787'
    },
    'Reflections': {
      gradient: 'from-[#f1e7d3] to-[#ddd0b8]',
      border: 'border-[#b8997a]',
      accent: '#6b4226',
      accentLight: '#b8997a'
    }
  };

  return (
    <section id="creations-learnings" className="py-20 bg-[#f5f0e6] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#b88865]/15 to-transparent blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#6b4226]/15 to-transparent blur-3xl"></div>
      <div className="absolute top-1/2 left-1/3 w-72 h-72 rounded-full bg-gradient-to-br from-[#8b5a3c]/10 to-transparent blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Lightbulb className="w-8 h-8 text-[#b88865] animate-pulse" />
            <p className="font-handwriting text-4xl gradient-text-animated">Personal musings</p>
            <Sparkles className="w-6 h-6 text-[#6b4226]" />
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-[#2d2a26] mb-4">
            <span className="font-serif">Creations & </span>
            <span className="font-display gradient-text-animated">Learnings</span>
          </h2>
          <p className="text-lg text-[#737373] max-w-2xl mx-auto">
            Tools, tinkerings, and lessons I've gathered along the way
          </p>
        </div>

        {/* Preview Cards Grid - 2 columns for cleaner layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-testid="musings-grid">
          {creationsAndLearnings.map((item, index) => {
            const style = categoryStyles[item.category] || categoryStyles['Product Thinking'];
            const isHovered = hoveredCard === item.id;

            return (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`musing-card-${item.id}`}
                className="block group"
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <Card
                  className={`bg-gradient-to-br ${style.gradient} ${style.border} backdrop-blur-sm border-2 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden h-full`}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    boxShadow: isHovered ? `0 25px 50px -12px ${style.accent}40` : undefined
                  }}
                >
                  {/* Live Preview Section */}
                  <div className="relative h-56 overflow-hidden bg-white/50">
                    {/* Iframe for live preview */}
                    <div className="absolute inset-0 overflow-hidden">
                      <iframe
                        src={item.url}
                        title={item.title}
                        className="w-[200%] h-[200%] border-0 pointer-events-none origin-top-left"
                        style={{
                          transform: 'scale(0.5)',
                          opacity: isHovered ? 1 : 0.92,
                          transition: 'opacity 0.3s ease'
                        }}
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin"
                      />
                    </div>

                    {/* Gradient overlay */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent transition-opacity duration-300 ${isHovered ? 'opacity-30' : 'opacity-50'}`}
                    ></div>

                    {/* Category badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <Badge
                        className="text-white border-none backdrop-blur-sm shadow-lg"
                        style={{ backgroundColor: style.accent }}
                      >
                        {item.category}
                      </Badge>
                    </div>

                    {/* Hover overlay with play indicator */}
                    <div
                      className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center backdrop-blur-md shadow-xl transform transition-transform duration-300 group-hover:scale-110"
                        style={{ backgroundColor: `${style.accent}e6` }}
                      >
                        <Play className="w-7 h-7 text-white ml-1" fill="white" />
                      </div>
                    </div>

                    {/* External link icon */}
                    <div className="absolute top-4 left-4 z-10">
                      <div
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg"
                      >
                        <ExternalLink className="w-5 h-5" style={{ color: style.accent }} />
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 bg-white/85 backdrop-blur-sm">
                    {/* Title */}
                    <h3 className="text-lg font-bold text-[#2d2a26] mb-3 group-hover:text-[#8b5a3c] transition-colors font-serif leading-tight">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[#666666] text-sm mb-4 leading-relaxed line-clamp-3">
                      {item.description}
                    </p>

                    {/* Preview hint with styled box */}
                    <div
                      className="rounded-lg p-3 mb-4"
                      style={{
                        backgroundColor: `${style.accentLight}40`,
                        borderLeft: `3px solid ${style.accent}`
                      }}
                    >
                      <p className="text-[#737373] text-xs italic">
                        {item.preview}
                      </p>
                    </div>

                    {/* CTA */}
                    <div
                      className="flex items-center gap-2 font-medium group-hover:gap-3 transition-all"
                      style={{ color: style.accent }}
                    >
                      <span className="text-sm">Explore this</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Card>
              </a>
            );
          })}
        </div>

        {/* Living thoughts CTA */}
        <div className="text-center mt-16">
          <div className="inline-block bg-white/80 backdrop-blur-sm border border-[#e0d0b8] rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow hover:-translate-y-1 duration-300">
            <p className="text-lg mb-2">
              <span className="font-handwriting text-3xl gradient-text-animated">These are living thoughts!</span>
            </p>
            <p className="text-[#999999] text-sm">
              They evolve as I learn more. Have a different take? Let's discuss!
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreationsLearnings;
